import { useCallback } from 'react';

import {
  GetV2PrototypesShareReviewsByShareTokenQuery,
  TCreatePrototypeReviewDto,
  TFinishReviewDto,
  TReviewFeedbackDto,
  useAvApi,
} from '@app-velocity/av-api-v2';

import { useSimulationToken, useUserReviewToken } from './useCreateToken';

type TokenType = 'testing' | 'report';

export const usePasswordProtectedQueries = () => {
  const api = useAvApi();
  const { getToken: getSimulationToken, setToken: setSimulationToken } =
    useSimulationToken();
  const { getToken: getReviewToken, setToken: setReviewToken } =
    useUserReviewToken();

  const reviewToken = getReviewToken();
  const simulationToken = getSimulationToken();

  const getRequestOptions = useCallback(
    (type: TokenType) => {
      const token = type === 'testing' ? reviewToken : simulationToken;

      if (!token) return {};

      return {
        headers: {
          'X-Prototype-Password': token,
        },
      };
    },
    [simulationToken, reviewToken]
  );

  const handleRequest = useCallback(
    async <T>(
      fn: () => Promise<{ data: T }>,
      type: 'testing' | 'report'
    ): Promise<T> => {
      try {
        const res = await fn();

        return res.data;
      } catch (error) {
        const err = error as Error & {
          response?: { status?: number };
        };

        if (err?.response?.status === 409) {
          // Clear invalid token
          if (type === 'testing') {
            setReviewToken('');
          } else {
            setSimulationToken('');
          }
        }

        throw err;
      }
    },
    [setSimulationToken, setReviewToken]
  );

  const getSharedScenario = useCallback(
    async (shareToken: string, type: TokenType) => {
      if (!shareToken) return null;
      const { headers } = getRequestOptions(type);

      return handleRequest(
        () =>
          api.prototypesShare.getV2PrototypesShareByShareToken(
            { shareToken },
            { headers }
          ),
        type
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  const getSharedScenarioPage = useCallback(
    async (shareToken: string, pageId: string, type: TokenType) => {
      const { headers } = getRequestOptions(type);

      return handleRequest(
        () =>
          api.prototypesShare.getV2PrototypesSharePagesByShareTokenAndPageId(
            { shareToken, pageId },
            { headers }
          ),
        type
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  const getSharedScenarioPartial = useCallback(
    async (shareToken: string, partialId: string, type: TokenType) => {
      const { headers } = getRequestOptions(type);

      return handleRequest(
        () =>
          api.prototypesShare.getV2PrototypesSharePartialsByShareTokenAndPartialId(
            { shareToken, partialId },
            { headers }
          ),
        type
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  const getSharedPrototypeFullSummary = useCallback(
    async (shareToken: string) => {
      if (!shareToken) return null;

      const { headers } = getRequestOptions('report');

      return handleRequest(
        () =>
          api.prototypesShare.getV2PrototypesShareSummaryFullByShareToken(
            { shareToken },
            { headers }
          ),
        'report'
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  const getScenarioReviewsByShareToken = useCallback(
    async (
      shareToken: string,
      query: GetV2PrototypesShareReviewsByShareTokenQuery
    ) => {
      if (!shareToken) return null;

      const { headers } = getRequestOptions('report');

      return handleRequest(
        () =>
          api.prototypesShare.getV2PrototypesShareReviewsByShareToken(
            { shareToken },
            query,
            { headers }
          ),
        'report'
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  const finishReviewByAuthUser = useCallback(
    async (shareToken: string, body: TFinishReviewDto) => {
      if (!shareToken) return null;

      const { headers } = getRequestOptions('testing');

      return handleRequest(
        () =>
          api.prototypesShare.postV2PrototypesShareReviewFinishByShareToken(
            body,
            { shareToken },
            { headers }
          ),
        'report'
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  const finishReviewByAnonymousUser = useCallback(
    async (shareToken: string, reviewId: string, body: TFinishReviewDto) => {
      if (!shareToken) return null;

      const { headers } = getRequestOptions('testing');

      return handleRequest(
        () =>
          api.prototypesAnonymousReview.postV2PrototypesShareAnonymousReviewFinishByShareTokenAndReviewId(
            body,
            { shareToken, reviewId },
            { headers }
          ),
        'report'
      );
    },
    [api, getRequestOptions, handleRequest]
  );
  
  const addFeedbackByAuthUser = useCallback(
    async (shareToken: string, reviewId: string, body: TReviewFeedbackDto) => {
      if (!shareToken) return null;

      const { headers } = getRequestOptions('testing');

      return handleRequest(
        () =>
          api.prototypesShare.postV2PrototypesShareReviewFeedbackByShareTokenAndReviewId(
            body,
            { shareToken, reviewId },
            { headers }
          ),
        'report'
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  const addFeedbackByAnonymousUser = useCallback(
    async (shareToken: string, reviewId: string, body: TReviewFeedbackDto) => {
      if (!shareToken) return null;

      const { headers } = getRequestOptions('testing');

      return handleRequest(
        () =>
          api.prototypesAnonymousReview.postV2PrototypesShareAnonymousReviewFeedbackByShareTokenAndReviewId(
            body,
            { shareToken, reviewId },
            { headers }
          ),
        'report'
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  const startReviewByAuthUser = useCallback(
    async (shareToken: string) => {
      if (!shareToken) return null;

      const { headers } = getRequestOptions('testing');

      return handleRequest(
        () =>
          api.prototypesShare.postV2PrototypesShareReviewByShareToken(
            null,
            { shareToken },
            { headers }
          ),
        'report'
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  const startReviewByAnonymousUser = useCallback(
    async (shareToken: string, body: TCreatePrototypeReviewDto) => {
      if (!shareToken) return null;

      const { headers } = getRequestOptions('testing');

      return handleRequest(
        () =>
          api.prototypesAnonymousReview.postV2PrototypesShareAnonymousReviewByShareToken(
            body,
            { shareToken },
            { headers }
          ),
        'report'
      );
    },
    [api, getRequestOptions, handleRequest]
  );

  return {
    getSharedScenario,
    getSharedScenarioPage,
    getSharedScenarioPartial,
    getSharedPrototypeFullSummary,
    getScenarioReviewsByShareToken,
    finishReviewByAnonymousUser,
    finishReviewByAuthUser,
    startReviewByAnonymousUser,
    startReviewByAuthUser,
    addFeedbackByAnonymousUser,
    addFeedbackByAuthUser
  };
};
